import { GetStaticPropsContext } from 'next';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs } from '~/components/breadcrumbs';
import { AcceleratorsSection } from '~/features/enterprise/landing-pages/components/accelerators-section';
import { EnterpriseLandingFeatureCardsSection } from '~/features/enterprise/landing-pages/components/enterprise-landing-feature-cards-section';
import { EnterpriseVideoHero } from '~/features/enterprise/landing-pages/components/enterprise-video-hero';
import { FooterBannerSection } from '~/features/enterprise/landing-pages/components/footer-banner-section';
import { SkillsIntelligenceSection } from '~/features/enterprise/landing-pages/components/skills-intelligence-section';
import humanAiSupportConfig from '~/features/enterprise/landing-pages/configs/human-ai-support.json';
import { PerformanceIcon } from '~/features/enterprise/landing-pages/icons';
import { EnterpriseHumanAiSupportLandingPageConfig } from '~/features/enterprise/landing-pages/models/enterprise-platform-landing-page-config';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import {
  LogosCarousel,
  StaticLogosCarouselProps,
  transformStaticLogosCarouselProps,
} from '~/features/page-template/templates/enterprise-overview/components/logos-carousel';

type HumanAiSupportLandingPageProps = {
  config: EnterpriseHumanAiSupportLandingPageConfig;
  page: Page;
  staticLogosCarouselProps: StaticLogosCarouselProps;
};

/**
 * Human+AI Support enterprise landing page.
 */
export default function EnterpriseHumanAiSupportLandingPage({
  config,
  page,
  staticLogosCarouselProps,
}: HumanAiSupportLandingPageProps) {
  const { t } = useTranslation();
  const { accelerators, benefits, footerBanner, hero, skillsIntelligence, slug } = config;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);

  const breadcrumbs = config.breadcrumbs.map((bc) => ({
    label: t(`enterprise.${slug}.breadcrumbs.${bc.id}`),
    href: bc.href,
  }));

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <Breadcrumbs items={breadcrumbs} />
        <EnterpriseVideoHero analyticsLabels={analyticsLabels} hero={hero} slug={slug} />
        <LogosCarousel {...staticLogosCarouselProps} />
        {accelerators && <AcceleratorsSection config={accelerators} slug={slug} />}
        {skillsIntelligence && <SkillsIntelligenceSection config={skillsIntelligence} slug={slug} />}
        {benefits && (
          <EnterpriseLandingFeatureCardsSection
            slug={slug}
            config={{
              ...benefits,
              headerIcon: <PerformanceIcon color='grey.500' height='18px' width='18px' />,
            }}
          />
        )}
        <FooterBannerSection footerBanner={footerBanner} slug={slug} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return { notFound: true };
  }

  const config = humanAiSupportConfig as EnterpriseHumanAiSupportLandingPageConfig;

  const [page, staticLogosCarouselProps] = await Promise.all([
    getPage('/enterprise/overview/[slug]'),
    transformStaticLogosCarouselProps(),
  ]);

  if (!page) {
    return { notFound: true };
  }

  return {
    props: {
      page,
      config,
      staticLogosCarouselProps,
    },
  };
}
